import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import PropTypes from 'prop-types';
import { ShoppingCart, Search, Truck, Calendar, Box, FileSpreadsheet } from 'lucide-react';
import {
  purchaseOrdersOf, effectiveBranchesOf,
  findCatalogProduct, variantLabelOf, activeSuppliersOf, getOrgSettings,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { FilterPopover } from '../../components/FilterPopover';

export const PurchaseReportTab = ({ org, branchId }) => {
  const { version: dataVersion } = useSharedStore();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ branches: [], suppliers: [], statuses: [], dateRange: {} });

  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id, dataVersion]);
  const branchesEnabled = useMemo(() => getOrgSettings(org.id).branchesEnabled, [org.id, dataVersion]);
  const suppliers = useMemo(() => activeSuppliersOf(org.id), [org.id, dataVersion]);

  const list = useMemo(() => {
    // Pull purchases from all locations/branches for org-wide report
    const locations = branchId ? [branchId] : branches.map(b => b.id);
    const allPurchases = [];
    const seen = new Set();
    for (const locId of locations) {
      for (const p of purchaseOrdersOf(org.id, locId)) {
        if (!seen.has(p.id)) { seen.add(p.id); allPurchases.push(p); }
      }
    }

    return allPurchases
      .filter((p) => {
        if (filters.branches?.length > 0 && !filters.branches.includes(p.locationId)) return false;
        if (filters.suppliers?.length > 0 && !filters.suppliers.includes(p.supplier)) return false;
        if (filters.statuses?.length > 0 && !filters.statuses.includes(p.status)) return false;
        if (filters.dateRange?.from && p.date && p.date.slice(0, 10) < filters.dateRange.from) return false;
        if (filters.dateRange?.to && p.date && p.date.slice(0, 10) > filters.dateRange.to) return false;
        if (search.trim()) {
          const q = search.toLowerCase();
          const prod = findCatalogProduct(p.productId);
          if (!((prod?.name || '').toLowerCase().includes(q) || (p.supplier || '').toLowerCase().includes(q))) return false;
        }
        return true;
      })
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [search, filters, org.id, branchId, branches, dataVersion]);

  const totals = useMemo(() => ({
    amount: list.reduce((s, p) => s + (p.amount || 0), 0),
    qty: list.reduce((s, p) => s + (p.qty || 0), 0),
  }), [list]);

  const accent = org.theme.primary;

  const exportExcel = () => {
    const rows = [
      ['Date', 'Product', 'Variant', 'Supplier', 'Branch', 'Cost/Unit', 'Status', 'Qty', 'Amount'],
      ...list.map((p) => {
        const prod = findCatalogProduct(p.productId);
        const branch = branches.find(b => b.id === p.locationId);
        return [
          p.date ? p.date.slice(0, 10) : '',
          prod?.name || p.productId,
          variantLabelOf(prod, p.variantId) || '',
          p.supplier,
          branch?.name || p.locationId || '',
          p.costPrice || 0,
          p.status,
          p.qty,
          p.amount || 0,
        ];
      }),
      ['', '', '', '', 'TOTAL', '', '', totals.qty, totals.amount],
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchase Report');
    XLSX.writeFile(workbook, `purchase-report-${org.name.replace(/\s+/g, '-')}.xlsx`);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header bar */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3">
        {/* Filters row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between flex-wrap">
          <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-lg">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search supplier or product…"
                className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none" />
            </div>

            {/* Filter popover */}
            <FilterPopover
              align="right"
              accent={accent}
              initialFilters={filters}
              onApply={setFilters}
              categories={[
                { id: 'dateRange', label: 'Date Range', type: 'date_range' },
                ...(branches.length > 1 && branchesEnabled ? [{ id: 'branches', label: 'Branch', options: branches.map(b => ({ id: b.id, label: b.name })) }] : []),
                ...(suppliers.length > 0 ? [{ id: 'suppliers', label: 'Supplier', options: suppliers.map(s => ({ id: s.supplierName, label: s.supplierName })) }] : []),
                { id: 'statuses', label: 'Status', options: [{ id: 'Received', label: 'Received' }, { id: 'Pending', label: 'Pending' }, { id: 'Cancelled', label: 'Cancelled' }] },
              ]}
            />
          </div>

          {/* Totals + Export */}
          <div className="flex items-center gap-3 shrink-0 flex-wrap">
            <button type="button" onClick={exportExcel}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-white border border-[var(--theme-tertiary)] text-slate-600 hover:border-[var(--theme-primary)] hover:text-[var(--theme-primary)] shadow-sm text-[11px] font-bold uppercase tracking-[0.12em] transition-colors shrink-0">
              <FileSpreadsheet size={13} /> Export Excel
            </button>
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left">
            <thead className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 border-b border-[var(--theme-tertiary)] bg-slate-50">
              <tr>
                <th className="px-5 py-3">Date</th>
                <th className="px-3 py-3">Product</th>
                <th className="px-3 py-3">Variant</th>
                <th className="px-3 py-3">Supplier</th>
                {!branchId && <th className="px-3 py-3">Branch</th>}
                <th className="px-3 py-3 text-right">Cost/Unit</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Qty</th>
                <th className="px-3 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-tertiary)]">
              {list.map((p) => {
                const prod = findCatalogProduct(p.productId);
                const branch = branches.find(b => b.id === p.locationId);
                const statusColor = p.status === 'Received' ? 'text-emerald-600'
                  : p.status === 'Pending' ? 'text-amber-600'
                    : 'text-rose-600';
                return (
                  <tr key={p.id} className="hover:bg-slate-50 group">
                    <td className="px-5 py-3 text-[11px] text-slate-500">
                      {p.date ? p.date.slice(0, 10) : '—'}
                    </td>
                    <td className="px-3 py-3 text-[13px] font-semibold text-[#0d0d0d]">
                      {prod?.name || p.productId}
                    </td>
                    <td className="px-3 py-3 text-[11px] text-slate-400">{variantLabelOf(prod, p.variantId) || '—'}</td>
                    <td className="px-3 py-3 text-[12px] text-slate-700">
                      {p.supplier}
                    </td>
                    {!branchId && <td className="px-3 py-3 text-[12px] text-slate-500">{branch?.name || '—'}</td>}
                    <td className="px-3 py-3 text-[12px] text-slate-700 text-right">₹{(p.costPrice || 0).toFixed(2)}</td>
                    <td className={`px-3 py-3 text-[11px] font-bold uppercase tracking-[0.12em] ${statusColor}`}>
                      {p.status}
                    </td>
                    <td className="px-3 py-3 text-[12px] text-slate-700 text-right font-semibold">{p.qty}</td>
                    <td className="px-3 py-3 text-[12px] font-extrabold text-[#0d0d0d] text-right">₹{(p.amount || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><td colSpan={branchId ? 8 : 9} className="px-5 py-16 text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">No purchase records found.</td></tr>
              )}
            </tbody>
            {list.length > 0 && (
              <tfoot className="border-t-2 border-[var(--theme-tertiary)] bg-slate-50 text-[11px] font-extrabold text-[#0d0d0d]">
                <tr>
                  <td colSpan={branchId ? 4 : 5} className="px-5 py-3 uppercase tracking-[0.14em] text-slate-500">Total ({list.length} records)</td>
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3 text-right">{totals.qty}</td>
                  <td className="px-3 py-3 text-right" style={{ color: accent }}>₹{totals.amount.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>
    </div>
  );
};

PurchaseReportTab.propTypes = {
  org: PropTypes.object.isRequired,
  branchId: PropTypes.string,
};
